/**
 * Experience Graph Page
 *
 * Interactive viewer for the canonical experience graph.
 * Shows nodes (activities, roles, companies, skills, etc.) and their connections.
 * Supports filtering by node type, search, and node detail inspection.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useBreadcrumbs } from '../../contexts/BreadcrumbContext';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  fetchExperienceGraph,
  fetchGraphNeighborhood,
  ingestCurrentResume,
} from '../../services/graph';
import {
  GraphNodeBase,
  GraphEdge,
  GraphNodeType,
  ExperienceGraph,
} from '../../types/graph';
import CareerTimeline from '../../components/CareerTimeline/CareerTimeline';
import './ExperienceGraphPage.css';

type ViewMode = 'cards' | 'timeline';

// Node type display config
const NODE_TYPE_CONFIG: Record<GraphNodeType, { label: string; emoji: string; color: string }> = {
  company: { label: 'Companies', emoji: '🏢', color: '#4a90d9' },
  organization: { label: 'Organizations', emoji: '🏛️', color: '#7b68ee' },
  role: { label: 'Roles', emoji: '💼', color: '#2ecc71' },
  project: { label: 'Projects', emoji: '📁', color: '#e67e22' },
  activity: { label: 'Activities', emoji: '⚡', color: '#e74c3c' },
  knowledgeItem: { label: 'Knowledge', emoji: '🧠', color: '#9b59b6' },
  credential: { label: 'Credentials', emoji: '📜', color: '#f39c12' },
  educationRecord: { label: 'Education', emoji: '🎓', color: '#1abc9c' },
  outcome: { label: 'Outcomes', emoji: '📊', color: '#3498db' },
  evidence: { label: 'Evidence', emoji: '📎', color: '#95a5a6' },
};

const ALL_NODE_TYPES: GraphNodeType[] = [
  'company', 'organization', 'role', 'project', 'activity',
  'knowledgeItem', 'credential', 'educationRecord', 'outcome', 'evidence',
];

export default function ExperienceGraphPage() {
  const { user } = useAuth();
  const { setPageCrumbs } = useBreadcrumbs();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [graph, setGraph] = useState<ExperienceGraph | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState<Set<GraphNodeType>>(new Set(ALL_NODE_TYPES));
  const [selectedNode, setSelectedNode] = useState<GraphNodeBase | null>(null);
  const [neighborEdges, setNeighborEdges] = useState<GraphEdge[]>([]);
  const [neighborNodes, setNeighborNodes] = useState<GraphNodeBase[]>([]);
  const [ingesting, setIngesting] = useState(false);
  const [ingestResult, setIngestResult] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('timeline');

  // Set breadcrumbs
  useEffect(() => {
    setPageCrumbs([{ label: t('graph.title') || 'Experience Graph' }]);
  }, [setPageCrumbs, t]);

  // Load graph
  const loadGraph = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchExperienceGraph();
      setGraph(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load graph');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadGraph();
  }, [loadGraph]);

  // Select a node and load its neighborhood
  const handleNodeClick = async (node: GraphNodeBase) => {
    setSelectedNode(node);
    try {
      const nh = await fetchGraphNeighborhood(node.nodeType, node.id);
      setNeighborEdges(nh.edges);
      setNeighborNodes(nh.neighbors);
    } catch (err) {
      console.error('Failed to load neighborhood:', err);
      setNeighborEdges([]);
      setNeighborNodes([]);
    }
  };

  // Toggle filter
  const toggleFilter = (nodeType: GraphNodeType) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(nodeType)) {
        next.delete(nodeType);
      } else {
        next.add(nodeType);
      }
      return next;
    });
  };

  // Ingest resume
  const handleIngest = async () => {
    setIngesting(true);
    setIngestResult(null);
    try {
      const result = await ingestCurrentResume();
      setIngestResult(`${result.created} created, ${result.merged} merged (${result.totalCandidates} candidates)`);
      await loadGraph(); // Refresh
    } catch (err: any) {
      setIngestResult(`Error: ${err.message}`);
    } finally {
      setIngesting(false);
    }
  };

  // Filtered & searched nodes
  const filteredNodes = (graph?.nodes || []).filter((n) => {
    if (!activeFilters.has(n.nodeType)) return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        n.canonicalLabel.toLowerCase().includes(term) ||
        n.aliases.some((a) => a.toLowerCase().includes(term))
      );
    }
    return true;
  });

  // Group nodes by type for display
  const groupedNodes = new Map<GraphNodeType, GraphNodeBase[]>();
  for (const node of filteredNodes) {
    if (!groupedNodes.has(node.nodeType)) {
      groupedNodes.set(node.nodeType, []);
    }
    groupedNodes.get(node.nodeType)!.push(node);
  }

  // Stats
  const totalNodes = graph?.nodes.length || 0;
  const totalEdges = graph?.edges.length || 0;

  if (loading) {
    return (
      <div className="graph-page" data-testid="graph-page">
        <div className="graph-loading" data-testid="graph-loading">{t('common.loading') || 'Loading...'}</div>
      </div>
    );
  }

  return (
    <div className="graph-page" data-testid="graph-page">
      <header className="graph-header">
        <div className="graph-header-left">
          <button className="graph-back-btn" onClick={() => navigate('/dashboard')} title="Back to Dashboard">
            ← Dashboard
          </button>
          <h1 data-testid="graph-heading">{t('graph.title') || 'Experience Graph'}</h1>
          <span className="graph-stats" data-testid="graph-stats">
            {totalNodes} nodes · {totalEdges} edges
          </span>
        </div>
        <div className="graph-header-actions">
          <div className="graph-view-toggle" data-testid="graph-view-toggle">
            <button
              className={`view-toggle-btn ${viewMode === 'timeline' ? 'active' : ''}`}
              onClick={() => setViewMode('timeline')}
              title="Timeline view"
            >
              📅 Timeline
            </button>
            <button
              className={`view-toggle-btn ${viewMode === 'cards' ? 'active' : ''}`}
              onClick={() => setViewMode('cards')}
              title="Card view"
            >
              🗂️ Cards
            </button>
          </div>
          <button
            className="graph-ingest-btn"
            data-testid="graph-ingest-btn"
            onClick={handleIngest}
            disabled={ingesting}
          >
            {ingesting ? (t('common.loading') || 'Processing...') : (t('graph.ingest') || 'Ingest Resume')}
          </button>
          {ingestResult && <span className="graph-ingest-result" data-testid="graph-ingest-result">{ingestResult}</span>}
        </div>
      </header>

      {error && <div className="graph-error" data-testid="graph-error">{error}</div>}

      <div className="graph-layout">
        {/* Filters sidebar */}
        <aside className="graph-filters" data-testid="graph-filters">
          <div className="graph-search">
            <input
              type="text"
              placeholder={t('graph.search') || 'Search nodes...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <h3>{t('graph.nodeTypes') || 'Node Types'}</h3>
          {ALL_NODE_TYPES.map((nt) => {
            const config = NODE_TYPE_CONFIG[nt];
            const count = (graph?.nodes || []).filter((n) => n.nodeType === nt).length;
            return (
              <label key={nt} className="graph-filter-item">
                <input
                  type="checkbox"
                  checked={activeFilters.has(nt)}
                  onChange={() => toggleFilter(nt)}
                />
                <span className="graph-filter-emoji">{config.emoji}</span>
                <span className="graph-filter-label">{config.label}</span>
                <span className="graph-filter-count">{count}</span>
              </label>
            );
          })}
        </aside>

        {/* Main content */}
        <main className="graph-content">
          {totalNodes === 0 ? (
            <div className="graph-empty" data-testid="graph-empty">
              <p>{t('graph.empty') || 'No graph data yet. Click "Ingest Resume" to populate your experience graph from your latest resume.'}</p>
            </div>
          ) : viewMode === 'timeline' ? (
            <CareerTimeline
              nodes={graph?.nodes || []}
              edges={graph?.edges || []}
              onNodeClick={handleNodeClick}
              selectedNodeId={selectedNode?.id}
            />
          ) : (
            <div className="graph-node-groups">
              {ALL_NODE_TYPES.filter((nt) => groupedNodes.has(nt)).map((nt) => {
                const config = NODE_TYPE_CONFIG[nt];
                const nodes = groupedNodes.get(nt)!;
                return (
                  <section key={nt} className="graph-node-group">
                    <h2>
                      <span className="graph-group-emoji">{config.emoji}</span>
                      {config.label}
                      <span className="graph-group-count">({nodes.length})</span>
                    </h2>
                    <div className="graph-node-cards">
                      {nodes.map((node) => (
                        <div
                          key={node.id}
                          className={`graph-node-card ${selectedNode?.id === node.id ? 'selected' : ''}`}
                          style={{ borderLeftColor: config.color }}
                          onClick={() => handleNodeClick(node)}
                        >
                          <div className="graph-node-card-title">{node.canonicalLabel}</div>
                          {node.aliases.length > 1 && (
                            <div className="graph-node-card-aliases">
                              {node.aliases.filter((a) => a !== node.canonicalLabel).join(', ')}
                            </div>
                          )}
                          <div className="graph-node-card-meta">
                            <span className="confidence">
                              {Math.round((node.provenance?.confidence || 0) * 100)}%
                            </span>
                            <span className="source">{node.provenance?.sourceType}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>
          )}
        </main>

        {/* Detail panel */}
        {selectedNode && (
          <aside className="graph-detail-panel" data-testid="graph-detail-panel">
            <div className="graph-detail-header">
              <h3>
                {NODE_TYPE_CONFIG[selectedNode.nodeType]?.emoji}{' '}
                {selectedNode.canonicalLabel}
              </h3>
              <button
                className="graph-detail-close"
                onClick={() => setSelectedNode(null)}
              >
                ×
              </button>
            </div>

            <div className="graph-detail-section">
              <strong>{t('graph.type') || 'Type'}:</strong> {selectedNode.nodeType}
            </div>

            {selectedNode.aliases.length > 0 && (
              <div className="graph-detail-section">
                <strong>{t('graph.aliases') || 'Aliases'}:</strong>
                <div className="graph-detail-aliases">
                  {selectedNode.aliases.map((a, i) => (
                    <span key={i} className="graph-alias-chip">{a}</span>
                  ))}
                </div>
              </div>
            )}

            <div className="graph-detail-section">
              <strong>{t('graph.confidence') || 'Confidence'}:</strong>{' '}
              {Math.round((selectedNode.provenance?.confidence || 0) * 100)}%
            </div>

            {selectedNode.timeRange && (
              <div className="graph-detail-section">
                <strong>Period:</strong>{' '}
                <span className="graph-detail-timerange">
                  {selectedNode.timeRange.startDate || '?'} → {selectedNode.timeRange.endDate || 'Present'}
                  {selectedNode.timeRange.duration && ` (${selectedNode.timeRange.duration})`}
                </span>
              </div>
            )}

            {selectedNode.description && (
              <div className="graph-detail-section">
                <strong>Description:</strong>{' '}
                <p className="graph-detail-description">{selectedNode.description}</p>
              </div>
            )}

            {selectedNode.title && selectedNode.title !== selectedNode.canonicalLabel && (
              <div className="graph-detail-section">
                <strong>Title:</strong> {selectedNode.title}
              </div>
            )}

            {selectedNode.institution && (
              <div className="graph-detail-section">
                <strong>Institution:</strong> {selectedNode.institution}
                {selectedNode.degree && ` — ${selectedNode.degree}`}
                {selectedNode.field && ` in ${selectedNode.field}`}
              </div>
            )}

            {selectedNode.technologies && selectedNode.technologies.length > 0 && (
              <div className="graph-detail-section">
                <strong>Technologies:</strong>
                <div className="graph-detail-aliases">
                  {selectedNode.technologies.map((tech, i) => (
                    <span key={i} className="graph-alias-chip tech-chip">{tech}</span>
                  ))}
                </div>
              </div>
            )}

            {selectedNode.location && (
              <div className="graph-detail-section">
                <strong>Location:</strong> {selectedNode.location}
              </div>
            )}

            <div className="graph-detail-section">
              <strong>{t('graph.source') || 'Source'}:</strong>{' '}
              {selectedNode.provenance?.sourceType}
            </div>

            {neighborNodes.length > 0 && (
              <div className="graph-detail-section">
                <strong>{t('graph.connections') || 'Connections'} ({neighborNodes.length}):</strong>
                <div className="graph-detail-neighbors">
                  {neighborNodes.map((n) => (
                    <div
                      key={n.id}
                      className="graph-neighbor-item"
                      onClick={() => handleNodeClick(n)}
                    >
                      {NODE_TYPE_CONFIG[n.nodeType]?.emoji} {n.canonicalLabel}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {neighborEdges.length > 0 && (
              <div className="graph-detail-section">
                <strong>{t('graph.edges') || 'Edges'} ({neighborEdges.length}):</strong>
                <div className="graph-detail-edges">
                  {neighborEdges.map((e) => (
                    <div key={e.id} className="graph-edge-item">
                      <span className="edge-relation">{e.relationType.replace(/_/g, ' ')}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </aside>
        )}
      </div>
    </div>
  );
}
